import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, BookOpen, Users, CheckCircle, Send } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import {
  PageHeader, AppCard, AppCardHeader, AppCardTitle, AppCardContent, EmptyState,
  AppButton, StarRating, Textarea,
} from '../ui';
import Shimmer from '../Common/Shimmer';
import CommonToaster from '../Common/CommonToaster';
import CourseSwitcher from './CourseSwitcher';

/** The pill beside a section title saying whether it must be filled in. */
function SectionTag({ required = false }) {
  return (
    <span
      className={
        'tw-ml-2 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[0.6875rem] tw-font-semibold ' +
        (required
          ? 'tw-bg-primary/10 tw-text-primary'
          : 'tw-bg-surface-sunken tw-text-content-subtle')
      }
    >
      {required ? 'Required' : 'Optional'}
    </span>
  );
}

/** One labelled fact about the enrollment being rated. */
function ContextItem({ icon: Icon, label, value }) {
  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-text-content-muted">
      <Icon size={16} className="tw-shrink-0" aria-hidden="true" />
      <div className="tw-flex tw-flex-col">
        <span className="tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-content-subtle">
          {label}
        </span>
        <span className="tw-text-sm tw-font-semibold tw-text-content">{value || '-'}</span>
      </div>
    </div>
  );
}

/**
 * One question and its rating.
 *
 * The rating's accessible name is the question text, so a screen reader on the
 * third row hears the question rather than "3 of 5" with no context.
 */
function QuestionRow({ question, value, onChange, disabled }) {
  return (
    <div className="tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-2 tw-border-b tw-border-line-soft tw-py-3.5 last:tw-border-b-0 sm:tw-flex-row sm:tw-items-center sm:tw-gap-4">
      <p className="tw-m-0 tw-min-w-0 tw-flex-1 tw-text-sm tw-text-content">{question}</p>
      <StarRating
        value={value}
        onChange={disabled ? undefined : onChange}
        readOnly={disabled}
        label={question}
        size="lg"
      />
    </div>
  );
}

const TRAINER_QUESTIONS = [
  { key: 'trainerTeachingQuality', label: 'How would you rate the trainer\'s teaching quality?' },
  { key: 'trainerClarity', label: 'How clearly did the trainer explain concepts?' },
  { key: 'trainerDoubtHandling', label: 'How well were your doubts/questions handled?' },
  { key: 'trainerEngagement', label: 'How engaging were the sessions?' },
];

const COURSE_QUESTIONS = [
  { key: 'courseContent', label: 'How would you rate the overall course content?' },
  { key: 'coursePractice', label: 'How useful were the assignments/practice?' },
  { key: 'courseRelevance', label: 'How relevant was the course to real-world/industry needs?' },
  { key: 'courseDifficulty', label: 'Was the course difficulty level appropriate?' },
];

// The page is a narrow reading column; every section is a card in one stack.
const PAGE = 'tw-mx-auto tw-flex tw-max-w-[760px] tw-flex-col tw-gap-5';

const EMPTY_RATINGS = {
  trainerTeachingQuality: 0,
  trainerClarity: 0,
  trainerDoubtHandling: 0,
  trainerEngagement: 0,
  courseContent: 0,
  coursePractice: 0,
  courseRelevance: 0,
  courseDifficulty: 0,
};

// Pull the rating values out of an existing feedback document (defaulting to 0).
const ratingsFromFeedback = (fb) =>
  Object.keys(EMPTY_RATINGS).reduce((acc, key) => ({ ...acc, [key]: fb?.[key] || 0 }), {});

const StudentFeedbackPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // One entry per enrolled course-batch the student can give feedback for.
  const [enrollments, setEnrollments] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Form state for the currently-selected course.
  const [ratings, setRatings] = useState(EMPTY_RATINGS);
  const [suggestions, setSuggestions] = useState('');

  const selected = enrollments[selectedIdx] || null;
  const existingFeedback = selected?.feedback || null;
  const submitted = !!existingFeedback;
  const hasMultiple = enrollments.length > 1;

  const loadFeedback = useCallback(async () => {
    const { data } = await apiClient.get('/feedback/my');
    setEnrollments(data.enrollments || []);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await loadFeedback();
      } catch {
        setToast({ show: true, message: 'Failed to load feedback data.', type: 'error' });
      }
      setLoading(false);
    };
    fetchData();
  }, [loadFeedback]);

  // Whenever the selected course (or its feedback) changes, sync the form to it.
  useEffect(() => {
    const fb = enrollments[selectedIdx]?.feedback;
    setRatings(fb ? ratingsFromFeedback(fb) : EMPTY_RATINGS);
    setSuggestions(fb?.suggestions || '');
  }, [selectedIdx, enrollments]);

  const setRating = (key, val) => {
    setRatings((prev) => ({ ...prev, [key]: val }));
  };


  const courseRatingsValid = COURSE_QUESTIONS.every((q) => ratings[q.key] > 0);

  const handleSubmit = async () => {
    if (!selected?.batchId) {
      setToast({ show: true, message: 'No batch assigned for this course yet.', type: 'error' });
      return;
    }
    if (!courseRatingsValid) {
      setToast({ show: true, message: 'Please rate all course questions.', type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...ratings, suggestions, batchId: selected.batchId };
      // Send null for trainer ratings if not filled
      TRAINER_QUESTIONS.forEach((q) => {
        if (!payload[q.key]) payload[q.key] = null;
      });

      await apiClient.post('/feedback', payload);
      setToast({ show: true, message: 'Feedback submitted successfully! Thank you.', type: 'success' });
      // Reload to reflect the submitted state for this course (keeps selection).
      await loadFeedback();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to submit feedback.';
      setToast({ show: true, message: msg, type: 'error' });
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className={PAGE}>
        <PageHeader icon={MessageSquare} title="Feedback & Rating" description="Share your learning experience" />
        <Shimmer height={120} count={4} />
      </div>
    );
  }

  if (!selected) {
    return (
      <div className={PAGE}>
        <PageHeader icon={MessageSquare} title="Feedback & Rating" description="Share your learning experience" />
        <EmptyState
          icon={<BookOpen />}
          title="No active enrollment"
          description="You are not currently enrolled in any batch. Feedback can be submitted once you are assigned to a batch."
        />
      </div>
    );
  }

  return (
    <div className={PAGE}>
      <PageHeader icon={MessageSquare} title="Feedback & Rating" description="Share your learning experience" />

      {/* Course switcher — only when the student has more than one course */}
      {hasMultiple && (
        <CourseSwitcher
          enrollments={enrollments}
          selectedIdx={selectedIdx}
          onSelect={setSelectedIdx}
        />
      )}

      {/* Enrollment Context Card */}
      {/* A left rule in the brand colour marks this as the "what you are rating" card. */}
      <AppCard className="tw-border-l-[3px] tw-border-l-primary">
        <AppCardContent>
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-4 sm:tw-flex-row sm:tw-flex-wrap sm:tw-items-center sm:tw-gap-6">
            <ContextItem icon={BookOpen} label="Course" value={selected.courseName} />
            <ContextItem icon={Users} label="Batch" value={selected.batchName} />
            <ContextItem icon={Users} label="Trainer" value={selected.instructorName} />
            {submitted && (
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-sm tw-bg-success-soft tw-px-3 tw-py-1 tw-text-[0.8125rem] tw-font-semibold tw-text-success-fg sm:tw-ml-auto">
                <CheckCircle size={16} aria-hidden="true" />
                Submitted
              </span>
            )}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Trainer Feedback */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Users size={16} />
            Trainer Feedback
            <SectionTag />
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <p className="tw-mb-4 tw-rounded-sm tw-border-l-[3px] tw-border-l-line tw-bg-surface-sunken tw-px-3 tw-py-2 tw-text-[0.8125rem] tw-text-content-muted">
            Your trainer feedback is confidential and will not be visible to the trainer.
          </p>
          <div className="tw-flex tw-flex-col">
            {TRAINER_QUESTIONS.map((q) => (
              <QuestionRow
                key={q.key}
                question={q.label}
                value={ratings[q.key]}
                onChange={(val) => setRating(q.key, val)}
                disabled={submitted}
              />
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Course Feedback */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <BookOpen size={16} />
            Course Feedback
            <SectionTag required />
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-flex tw-flex-col">
            {COURSE_QUESTIONS.map((q) => (
              <QuestionRow
                key={q.key}
                question={q.label}
                value={ratings[q.key]}
                onChange={(val) => setRating(q.key, val)}
                disabled={submitted}
              />
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Suggestions */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <MessageSquare size={16} />
            Suggestions
            <SectionTag />
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <Textarea
            placeholder="Any suggestions or improvements you'd like to share..."
            rows={4}
            maxLength={2000}
            value={suggestions}
            onChange={(e) => setSuggestions(e.target.value)}
            disabled={submitted}
            aria-label="Suggestions"
          />
          <div className="tw-mt-1 tw-text-right tw-text-xs tw-text-content-subtle">
            {suggestions.length}/2000
          </div>
        </AppCardContent>
      </AppCard>

      {/* Submit Button */}
      {!submitted && (
        <div className="tw-flex tw-justify-end">
          <AppButton onClick={handleSubmit} disabled={submitting || !courseRatingsValid}>
            {submitting ? (
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            ) : (
              <Send size={16} />
            )}
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </AppButton>
        </div>
      )}

      {submitted && (
        <div
          role="status"
          className="tw-flex tw-items-center tw-gap-2.5 tw-rounded tw-border tw-border-success/20 tw-bg-success-soft tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-success-fg"
        >
          <CheckCircle size={22} className="tw-shrink-0" aria-hidden="true" />
          <p className="tw-m-0">
            Thank you for your feedback! Your response for this course has been recorded.
          </p>
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default StudentFeedbackPage;
